import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useDroppable } from '@dnd-kit/core';

import { CSS } from '@dnd-kit/utilities';

import { useSortable } from '@dnd-kit/sortable';

import { PlusIcon, QueueListIcon } from '@heroicons/react/24/outline';

import { useLanguage } from '@/state/language/LanguageProvider';

import {

  AE_ICON_SIZE,

  AE_MUTED,

  AE_BUILDER_SECTION_BADGE,

  AE_BUILDER_SUBSECTION_BADGE,

} from '@/components/assessment-engine/layout/aeTheme';

import type { AeTemplateDetail, AeTemplateQuestion, AeTemplateSection } from '@/types/assessmentEngine';

import { assessmentEngineService } from '@/services/assessmentEngineService';

import { BuilderActionBar } from './BuilderActionBar';

import { BuilderDragHandle } from './BuilderDragHandle';
import { SectionCollapseToggle } from './SectionCollapseToggle';

import { BuilderEntityIcon } from './BuilderEntityIcon';

import { BuilderIconButton } from './BuilderIconButton';

import { refreshAndPersistDimensions } from './dimensionSync';

import { InlineEditable } from './InlineEditable';

import { sectionParticipatesInScoring } from './sectionScoring';

import { DEMOGRAPHICS_SECTION_CODE } from './templateConfigTypes';

import { QuestionBlock, QuestionDraft } from './QuestionBlock';

import {
  beginStructureDelete,
  endStructureDeleteSoon,
  isStructureDeletePending,
} from './structureDeleteGuard';

import {

  defaultDisplayConfig,

  fromBuilderQuestionType,

  normalizeMatrixOptions,

  questionUsesScale,

} from './questionTypeConfig';

import {

  internalQuestionKey,

  internalSectionCode,

  questionDisplayNumber,

} from './numberingUtils';

import type { AeTemplateScalePreset } from './templateConfigTypes';

import { useDebouncedSave } from './useDebouncedSave';

import { useTemplateSaveError } from './useTemplateSaveError';

import {

  questionDndId,

  questionsDropDndId,

  sectionDndId,

  subsectionDropDndId,

  type BuilderQuestionDragData,

  type BuilderSectionDragData,

} from './builderDnd';

import { sectionSiblings } from './structureMoveUtils';

import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';



interface SectionBlockProps {

  templateId: string;

  template: AeTemplateDetail;

  section: AeTemplateSection;

  sectionNumber: number;

  parentSectionNumber?: number;

  parentSection?: AeTemplateSection;

  depth?: number;

  readOnly?: boolean;

  scalePresets?: AeTemplateScalePreset[];

  onReload: () => void;

}



interface SortableQuestionShellProps {

  question: AeTemplateQuestion;

  sectionId: string;

  index: number;

  readOnly?: boolean;

  dragLabel: string;

  children: React.ReactNode;

}



const SortableQuestionShell: React.FC<SortableQuestionShellProps> = ({

  question,

  sectionId,

  index,

  readOnly,

  dragLabel,

  children,

}) => {

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({

    id: questionDndId(question.id),

    disabled: readOnly,

    data: {

      kind: 'question',

      questionId: question.id,

      sectionId,

      index,

    } satisfies BuilderQuestionDragData,

  });



  const style = {

    transform: CSS.Transform.toString(transform),

    transition,

    opacity: isDragging ? 0.55 : 1,

  };



  return (

    <div ref={setNodeRef} style={style} className={isDragging ? 'relative z-10' : undefined}>

      <div className="flex gap-2 items-start">

        <BuilderDragHandle label={dragLabel} listeners={listeners} attributes={attributes} disabled={readOnly} />

        <div className="flex-1 min-w-0">{children}</div>

      </div>

    </div>

  );

};



/** Dimension (section) or sub-dimension (subsection) block with questions. */

export const SectionBlock: React.FC<SectionBlockProps> = ({

  templateId,

  template,

  section,

  sectionNumber,

  parentSectionNumber,

  parentSection,

  depth = 0,

  readOnly,

  scalePresets = [],

  onReload,

}) => {

  const { t } = useLanguage();

  const onSaveError = useTemplateSaveError();

  const [name, setName] = useState(section.name);

  const [overview, setOverview] = useState(section.overview ?? '');

  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const isSubsection = depth > 0;

  const isDemographics = section.code === DEMOGRAPHICS_SECTION_CODE;

  const participatesInScoring = sectionParticipatesInScoring(section.scoring_config);

  const isContextSection = !isSubsection && !participatesInScoring && !isDemographics;

  const entityKind = isSubsection ? 'subsection' : 'section';

  const badgeClass = isSubsection ? AE_BUILDER_SUBSECTION_BADGE : AE_BUILDER_SECTION_BADGE;

  const namePlaceholder = isSubsection

    ? t('assessmentEngine.builder.subDimensionName')

    : t('assessmentEngine.builder.dimensionName');

  const dragLabel = isSubsection

    ? t('assessmentEngine.builder.dragSubsection')

    : t('assessmentEngine.builder.dragSection');

  const dragQuestionLabel = t('assessmentEngine.builder.dragQuestion');
  const collapseLabel = expanded
    ? isSubsection
      ? t('assessmentEngine.builder.collapseSubsection')
      : t('assessmentEngine.builder.collapseSection')
    : isSubsection
      ? t('assessmentEngine.builder.expandSubsection')
      : t('assessmentEngine.builder.expandSection');

  const collapsedSummary = useMemo(() => {
    if (expanded) return '';
    const parts: string[] = [];
    const qCount = section.questions.length;
    const subCount = section.subsections.length;
    if (qCount > 0) {
      parts.push(
        t('assessmentEngine.builder.sectionQuestionCount', { count: String(qCount) })
      );
    }
    if (!isSubsection && subCount > 0) {
      parts.push(
        t('assessmentEngine.builder.sectionSubsectionCount', { count: String(subCount) })
      );
    }
    return parts.join(' · ');
  }, [expanded, section.questions.length, section.subsections.length, isSubsection, t]);

  const siblings = useMemo(

    () => sectionSiblings(template, section, parentSection),

    [template, section, parentSection]

  );

  const siblingIndex = siblings.findIndex((s) => s.id === section.id);

  const sectionDraggable = !readOnly && !isDemographics;



  const {

    attributes: sectionAttributes,

    listeners: sectionListeners,

    setNodeRef: setSectionRef,

    transform: sectionTransform,

    transition: sectionTransition,

    isDragging: isSectionDragging,

  } = useSortable({

    id: sectionDndId(section.id),

    disabled: !sectionDraggable,

    data: {

      kind: 'section',

      sectionId: section.id,

      parentSectionId: section.parent_section_id ?? null,

      index: siblingIndex,

    } satisfies BuilderSectionDragData,

  });



  const { setNodeRef: setQuestionsDropRef, isOver: isQuestionsDropOver } = useDroppable({

    id: questionsDropDndId(section.id),

    disabled: readOnly || isDemographics,

  });



  const showSubsectionDrop = !isSubsection && participatesInScoring && !isDemographics;

  const { setNodeRef: setSubsectionDropRef, isOver: isSubsectionDropOver } = useDroppable({

    id: subsectionDropDndId(section.id),

    disabled: readOnly || !showSubsectionDrop,

  });



  const questionSortableIds = section.questions.map((q) => questionDndId(q.id));

  const subsectionSortableIds = section.subsections.map((s) => sectionDndId(s.id));



  const serverSectionRevision = useMemo(

    () => `${section.id}\0${section.name}\0${section.overview ?? ''}`,

    [section.id, section.name, section.overview]

  );



  useEffect(() => {

    setName(section.name);

    setOverview(section.overview ?? '');

  }, [serverSectionRevision, section.name, section.overview]);



  const persist = useCallback(async () => {

    if (readOnly) return;
    if (isStructureDeletePending(section.id)) return;

    setSaving(true);

    try {

      await assessmentEngineService.updateSection(templateId, section.id, {

        name,

        overview: overview.trim() || null,

      });

      await refreshAndPersistDimensions(templateId);

    } finally {

      setSaving(false);

    }

  }, [readOnly, templateId, section.id, name, overview]);



  useDebouncedSave(persist, [name, overview, readOnly], 800, { onError: onSaveError });



  const handleDelete = async () => {
    // Section trash removes the whole dimension — confirm with the section name.
    if (
      !window.confirm(
        t('assessmentEngine.builder.confirmDeleteSectionNamed', { name: section.name })
      )
    ) {
      return;
    }

    beginStructureDelete(section.id);
    for (const q of section.questions) {
      beginStructureDelete(q.id);
    }
    for (const sub of section.subsections) {
      beginStructureDelete(sub.id);
      for (const q of sub.questions) {
        beginStructureDelete(q.id);
      }
    }

    try {
      await assessmentEngineService.deleteSection(templateId, section.id);
      await refreshAndPersistDimensions(templateId);
      onReload();
    } finally {
      endStructureDeleteSoon(section.id);
      for (const q of section.questions) {
        endStructureDeleteSoon(q.id);
      }
      for (const sub of section.subsections) {
        endStructureDeleteSoon(sub.id);
        for (const q of sub.questions) {
          endStructureDeleteSoon(q.id);
        }
      }
    }
  };



  const handleCopy = async () => {

    await assessmentEngineService.copySection(templateId, section.id);

    await refreshAndPersistDimensions(templateId);

    onReload();

  };



  const handleAddQuestion = async () => {

    const idx = section.questions.length;

    const builderType = 'short_text' as const;

    const sectionCode = internalSectionCode(

      sectionNumber - 1,

      isSubsection ? (parentSectionNumber ?? 1) - 1 : undefined

    );

    await assessmentEngineService.createQuestion(templateId, {

      section_id: section.id,

      question_key: internalQuestionKey(sectionCode, idx),

      question_text: t('assessmentEngine.builder.newQuestionDefault'),

      question_type: fromBuilderQuestionType(builderType),

      sort_order: idx,

      is_required: true,

      display_config: defaultDisplayConfig(builderType),

    });

    onReload();

  };



  const handleAddSubsection = async () => {

    const label = t('assessmentEngine.builder.newSubDimensionDefault');

    const idx = section.subsections.length;

    await assessmentEngineService.createSection(templateId, {

      parent_section_id: section.id,

      code: internalSectionCode(idx, sectionNumber - 1),

      name: label,

      sort_order: idx,

    });

    await refreshAndPersistDimensions(templateId);

    onReload();

  };



  const saveQuestion = async (questionId: string, draft: QuestionDraft) => {

    const usesScale = questionUsesScale(draft.question_type);

    await assessmentEngineService.updateQuestion(templateId, questionId, {

      question_text: draft.question_text,

      question_type: fromBuilderQuestionType(draft.question_type),

      is_required: draft.is_required,

      scale_min: usesScale ? draft.scale_min : null,

      scale_max: usesScale ? draft.scale_max : null,

      allow_na: draft.allow_na,

      display_config: draft.display_config,

    });

    // Matrix statements are question-rows sharing scale columns; normalize so
    // option_value stays a short ROW_n key and never the full statement text.
    const optionsToSave =
      draft.question_type === 'matrix'
        ? normalizeMatrixOptions(draft.options)
        : draft.options;

    if (optionsToSave.length > 0) {

      await assessmentEngineService.replaceQuestionOptions(templateId, questionId, optionsToSave);

    }

  };



  const outerClass = isSubsection

    ? 'mt-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-hover-bg)]/30'

    : isContextSection

      ? 'rounded-xl border border-dashed border-[var(--color-border)] bg-[var(--color-hover-bg)]/20'

      : 'rounded-xl border-2 border-[var(--color-accent-bg)]/40 bg-[var(--color-surface-bg)] shadow-md';



  const headerBorder = expanded
    ? isSubsection
      ? 'border-b border-[var(--color-border)]'
      : isContextSection
        ? 'border-b border-dashed border-[var(--color-border)]'
        : 'border-b-2 border-[var(--color-accent-bg)]/30'
    : '';

  const headerClass = isSubsection
    ? `px-4 py-3 bg-[var(--color-hover-bg)]/50 ${headerBorder}`
    : isContextSection
      ? `px-5 py-4 bg-[var(--color-hover-bg)]/30 ${headerBorder}`
      : `px-5 py-4 bg-[var(--color-accent-bg)]/10 ${headerBorder}`;



  const sectionStyle = {

    transform: CSS.Transform.toString(sectionTransform),

    transition: sectionTransition,

    opacity: isSectionDragging ? 0.55 : 1,

  };



  return (

    <article ref={setSectionRef} style={sectionStyle} className={outerClass}>

      <header className={headerClass}>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">

          <div className="flex-1 space-y-2 min-w-0">

            <div className="flex items-center gap-2 flex-wrap min-w-0">

              <SectionCollapseToggle
                expanded={expanded}
                label={collapseLabel}
                onToggle={() => setExpanded((open) => !open)}
              />

              <BuilderDragHandle

                label={dragLabel}

                listeners={sectionListeners}

                attributes={sectionAttributes}

                disabled={!sectionDraggable}

              />

              <span className={`${badgeClass} shrink-0`}>

                <BuilderEntityIcon kind={entityKind} />

              </span>

              <div className="flex-1 min-w-0">

                <InlineEditable

                  variant="heading"

                  value={name}

                  onChange={setName}

                  readOnly={readOnly}

                  placeholder={namePlaceholder}

                />

              </div>

              {saving && <span className={AE_MUTED}>{t('assessmentEngine.builder.saving')}</span>}

              {!expanded && collapsedSummary && (
                <span className={`${AE_MUTED} text-xs shrink-0`}>{collapsedSummary}</span>
              )}

            </div>

          </div>

          <BuilderActionBar disabled={readOnly} onCopy={handleCopy} onDelete={handleDelete} />

        </div>

        {expanded && (
          <InlineEditable

            variant="body"

            value={overview}

            onChange={setOverview}

            readOnly={readOnly}

            placeholder={t('assessmentEngine.builder.sectionOverviewHint')}

            rows={2}

          />
        )}

      </header>



      {expanded && (
      <div className={`${isSubsection ? 'p-4' : 'p-5'} space-y-4`}>

        <div

          ref={setQuestionsDropRef}

          className={`${isDemographics ? 'grid grid-cols-1 gap-2.5 md:grid-cols-2 lg:grid-cols-3' : 'space-y-4'} rounded-lg ${isQuestionsDropOver ? 'ring-2 ring-[var(--color-accent-bg)] ring-offset-2' : ''}`}

        >

          {section.questions.length === 0 && !readOnly && (

            <p

              className={`${AE_MUTED} text-center py-4 border border-dashed border-[var(--color-border)] rounded-lg`}

            >

              {t('assessmentEngine.builder.sectionEmptyHint')}

            </p>

          )}



          <SortableContext items={questionSortableIds} strategy={verticalListSortingStrategy}>

            {section.questions.map((q, qIdx) => (

              <SortableQuestionShell

                key={q.id}

                question={q}

                sectionId={section.id}

                index={qIdx}

                readOnly={readOnly}

                dragLabel={dragQuestionLabel}

              >

                <QuestionBlock

                  question={q}

                  questionNumber={questionDisplayNumber(qIdx)}

                  readOnly={readOnly}

                  scalePresets={scalePresets}

                  onSave={(draft) => saveQuestion(q.id, draft)}

                  onCopy={async () => {

                    await assessmentEngineService.copyQuestion(templateId, q.id);

                    onReload();

                  }}

                  onDelete={async () => {
                    // Capture id from this row — never rely on a shifting list index.
                    const questionId = q.id;
                    const preview = (q.question_text || q.question_key).trim().slice(0, 100);
                    if (
                      !window.confirm(
                        t('assessmentEngine.builder.confirmDeleteQuestionNamed', {
                          text: preview || t('assessmentEngine.builder.question'),
                        })
                      )
                    ) {
                      return;
                    }

                    beginStructureDelete(questionId);
                    try {
                      await assessmentEngineService.deleteQuestion(templateId, questionId);
                      onReload();
                    } finally {
                      endStructureDeleteSoon(questionId);
                    }
                  }}

                />

              </SortableQuestionShell>

            ))}

          </SortableContext>

        </div>



        <div

          ref={setSubsectionDropRef}

          className={`space-y-4 rounded-lg ${isSubsectionDropOver ? 'ring-2 ring-[var(--color-accent-bg)] ring-offset-2' : ''}`}

        >

          <SortableContext items={subsectionSortableIds} strategy={verticalListSortingStrategy}>

            {section.subsections.map((sub, subIdx) => (

              <SectionBlock

                key={sub.id}

                templateId={templateId}

                template={template}

                section={sub}

                sectionNumber={subIdx + 1}

                parentSectionNumber={sectionNumber}

                parentSection={section}

                depth={depth + 1}

                readOnly={readOnly}

                scalePresets={scalePresets}

                onReload={onReload}

              />

            ))}

          </SortableContext>

        </div>



        {!readOnly && (

          <div className="flex flex-wrap gap-1 pt-2 border-t border-dashed border-[var(--color-border)]">

            <BuilderIconButton

              label={t('assessmentEngine.builder.addQuestion')}

              onClick={() => void handleAddQuestion()}

            >

              <PlusIcon className={AE_ICON_SIZE} aria-hidden />

            </BuilderIconButton>

            {!isSubsection && participatesInScoring && (

              <BuilderIconButton

                label={t('assessmentEngine.builder.addSubDimension')}

                onClick={() => void handleAddSubsection()}

              >

                <QueueListIcon className={AE_ICON_SIZE} aria-hidden />

              </BuilderIconButton>

            )}

          </div>

        )}

      </div>
      )}

    </article>

  );

};


